import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useNewApiCall } from 'hooks/useNewApiCall';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { GlobalSettings } from 'components/services/GlobalSettingsService';

import {
  DepartmentsService,
  EmployeesService
} from '../../components/services';
import { AssetsService } from '../../components/services/AssetsService';
import { TaskService } from '../../components/services/TaskService';
import { PriorityOptions } from '../../constants';
import { createTaskSchemaWithDynamicFieldsAndRefinements } from '../../constants/schemas';
import { updateFieldFillingControll } from '../../state/schemaSlice/fieldFillingControllSlice';
import { RootState } from '../../state/store';
import {
  DropdownObjectType,
  Employee,
  FieldFilling,
  OperationAttainment,
  Task,
  TaskHistory,
  TaskType,
  TreeNode,
  TreeNodeBase
} from '../../types/interfaces';
import { convertOperationsOptionsToAttainments } from '../../utils/taskCard/convertOperationsOptionsToAttainments';
import { mapEmployeeAttainments } from '../../utils/taskCard/mapEmployeeAttainments';
import { mapEmployeesFromApiResponse } from '../../utils/taskCard/mapEmployeesFromApiResponse';
import { parseTimeToMilliseconds } from '../../utils/taskCard/parseTimeToMilliseconds';
import {
  isPlanningAlgorithmError,
  PLANNING_ALGORITHM_ERROR_MESSAGE
} from '../../utils/taskCard/taskAssignmentErrors';
import { buildEmployeeAssignmentRulesParams } from '../../utils/taskEmployeeAssignmentRules';

import { getFieldFillingControlStandalone } from './getFieldFillingControlStandalone';
import useOperationTypesService from './useOperationTypesService';
import useStatusTransitionsService from './useStatusTransitionsService';
import useTaskTypesService from './useTaskTypesService';

export function useTaskCardApi(
  taskProps: Task,
  pageId: string | undefined,
  activeIndex: number,
  globalSettings: GlobalSettings
): {
  loading: boolean;
  departmentsDropDownValue: TreeNode[];
  assetOptions: DropdownObjectType[];
  taskTypes: DropdownObjectType[];
  fieldFillingControll: FieldFilling[];
  taskHistory: Record<string, TaskHistory>;
  loadHistory: () => void;
  selectedPriority: DropdownObjectType | null;
  setSelectedPriority: React.Dispatch<
    React.SetStateAction<DropdownObjectType | null>
  >;
  callApi: (id: number | undefined) => Promise<void>;
  getAssetsApi: () => Promise<void>;
  getFieldFillingControll: (id: number) => Promise<void>;
  getTaskApi: (id: number | undefined, onlyStatus?: boolean) => Promise<void>;
  fetchTaskTypeDetails: (taskTypeId: number) => Promise<void>;
  ensureTaskTypeDetails: (taskTypeId: number) => Promise<TaskType | undefined>;
  selectedTaskTypeDetails: TaskType | null;
  recommendedEmployeesCount: number | null;
  executorsTable: Employee[];
  rawExecutorsData: Employee[];
  plannedOperationsOptions: TreeNodeBase[];
  plannedOperationsAttainments: OperationAttainment[];
  allOperationsAttainments: OperationAttainment[];
  actualOperationsEmployeeOptions: Employee[];
  form: UseFormReturn<Task>;
  showAllDepartments: boolean;
  toggleShowAllDepartments: () => void;
  showAllEmployees: boolean;
  toggleShowAllEmployees: () => void;
  fullDepartmentsTree: TreeNode[];
  fullExecutorsCatalog: Employee[];
  departmentPickerError: string | null;
  employeePickerError: string | null;
} {
  const dispatch = useDispatch();
  const fieldFillingControll = useSelector(
    (state: RootState) => state.fieldFillingControll
  );

  const [departmentsDropDownValue, setDepartmentsDropDownValue] = useState<
    TreeNode[]
  >([]);
  const [showAllDepartments, setShowAllDepartments] = useState(false);
  const [showAllEmployees, setShowAllEmployees] = useState(true);
  const [fullDepartmentsList, setFullDepartmentsList] = useState<TreeNode[]>(
    []
  );
  const [assetOptions, setAssetOptions] = useState<DropdownObjectType[]>([]);
  const [taskTypes, setTaskTypes] = useState<DropdownObjectType[]>([]);
  const [departmentPickerError, setDepartmentPickerError] = useState<
    string | null
  >(null);
  const [employeePickerError, setEmployeePickerError] = useState<string | null>(
    null
  );
  const [selectedTaskTypeDetails, setSelectedTaskTypeDetails] =
    useState<TaskType | null>(null);

  const [taskHistory, setTaskHistory] = useState<Record<string, TaskHistory>>(
    {}
  );
  const [selectedPriority, setSelectedPriority] =
    useState<DropdownObjectType | null>(null);
  const [recommendedEmployeesCount, setRecommendedEmployeesCount] = useState<
    number | null
  >(null);
  const [executorsTable, setExecutorsTable] = useState<Employee[]>([]);
  const [rawExecutorsData, setRawExecutorsData] = useState<Employee[]>([]);
  const [fullExecutorsRaw, setFullExecutorsRaw] = useState<Employee[]>([]);
  const [schema, setSchema] = useState(() => 
    createTaskSchemaWithDynamicFieldsAndRefinements(fieldFillingControll)
  );
  const fullExecutorsByDepartmentCacheRef = useRef<{
    departmentKey: string;
    employees: Employee[];
  } | null>(null);
  const previousDistrictRef = useRef<string>('');
  const [plannedOperationsOptions, setPlannedOperationsOptions] = useState<
    TreeNodeBase[]
  >([]);
  const [plannedOperationsAttainments, setPlannedOperationsAttainments] =
    useState<OperationAttainment[]>([]);
  const [loading, setLoading] = useState(false);
  const [allOperationsAttainments, setAllOperationsAttainments] = useState<
    OperationAttainment[]
  >([]);
  const [actualOperationsEmployeeOptions, setActualOperationsEmployeeOptions] =
    useState<Employee[]>([]);

  const { reset, setValue, watch, ...formMethods } = useForm<Task>({
    resolver: zodResolver(schema),
    defaultValues: taskProps
  });

  const isDepartmentInterval = (departmentId: string): boolean => {
    if (!departmentId) return false;

    const findNode = (nodes: TreeNode[], key: string): TreeNode | null => {
      for (const node of nodes) {
        if (node.key === key) return node;
        if (node.children) {
          const found = findNode(node.children, key);
          if (found) return found;
        }
      }
      return null;
    };

    const foundNode = findNode(departmentsDropDownValue, departmentId);
    return (foundNode as any)?.isIntervalType === true;
  };


  const asset = watch('asset');
  const department = watch('department');
  const taskType = watch('taskType');
  const plannedOperations = watch('plannedOperations');
  const employees = watch('employees');
  const interval = watch('interval');
  const plannedStartDate = watch('plannedStartDate');
  const plannedEndDate = watch('plannedEndDate');
  const operationTypesService = useOperationTypesService();
  const taskTypesService = useTaskTypesService();

    
  const isInterval = isDepartmentInterval(department?.key as string);

  useEffect(() => {
  // Если нужно передавать department в функцию
    const newSchema = createTaskSchemaWithDynamicFieldsAndRefinements(
      fieldFillingControll,
      isInterval
    );
    setSchema(newSchema);
  }, [department, fieldFillingControll]);

  const getPlannedOperationsAttaiments = useCallback(async () => {
    const operationTypeIds = plannedOperations.map((op: DropdownObjectType) =>
      Number(op.key)
    );
    const result =
      await operationTypesService.getOperationAttainments(operationTypeIds);
    if (result?.data?.isSuccess && result.data.value) {
      setPlannedOperationsAttainments(
        result.data.value.map((x) => ({
          ...x,
          id: x.operationType.id
        }))
      );
    }
  }, [plannedOperations]);

  useNewApiCall(getPlannedOperationsAttaiments, [plannedOperations]);

  useEffect(() => {
    if (!taskType?.key) {
      setRecommendedEmployeesCount(null);
      setValue('effort', null);
      setSelectedTaskTypeDetails(null);
    }
  }, [setValue, taskType]);

  const getDepartmentsByParams = useCallback(
    async (
      assetId: string,
      taskTypeId?: number,
      planStart?: string,
      planFinish?: string
    ) => {
      const params: {
        assetId: number;
        taskTypeId?: number;
        planStart?: string;
        planFinish?: string;
      } = {
        assetId: Number(assetId)
      };

      if (taskTypeId && taskTypeId !== 0) {
        params.taskTypeId = taskTypeId;
      }

      if (planStart) {
        params.planStart = planStart;
      }

      if (planFinish) {
        params.planFinish = planFinish;
      }

      const response = await DepartmentsService.getDepartments(params);
      if (isPlanningAlgorithmError((response as any).error ?? response.data)) {
        setDepartmentPickerError(PLANNING_ALGORITHM_ERROR_MESSAGE);
        return [];
      }
      setDepartmentPickerError(null);
      const filtered = response?.data || [];

      return filtered;
    },
    []
  );

  const getDepartmentsApi = useCallback(
    async (passedAsset?: TreeNode) => {
      try {
        setDepartmentPickerError(null);
        const currentAsset = passedAsset || asset;
        const assetKey = currentAsset?.key;
        const taskTypeId = taskType?.key ? Number(taskType.key) : undefined;
        const isPlanDatesEnabled =
          globalSettings?.plan_dates_department_check ?? true;
        const isTaskTypeEnabled =
          globalSettings?.taskType_department_check ?? true;

        const needsFiltering = assetKey && !showAllDepartments;

        if (
          needsFiltering &&
          (!assetKey ||
            (isTaskTypeEnabled && !taskType?.key) ||
            (isPlanDatesEnabled && (!plannedStartDate || !plannedEndDate)))
        ) {
          return;
        }

        if (fullDepartmentsList.length === 0) {
          const allDeptsResponse = await DepartmentsService.getDepartments();
          const allDepartments = allDeptsResponse?.data || [];
          setFullDepartmentsList(allDepartments);
          setDepartmentsDropDownValue(
            needsFiltering
              ? await getDepartmentsByParams(
                  assetKey,
                  isTaskTypeEnabled ? taskTypeId : undefined,
                  isPlanDatesEnabled ? plannedStartDate : undefined,
                  isPlanDatesEnabled ? plannedEndDate : undefined
                )
              : allDepartments
          );
          return;
        }

        setDepartmentsDropDownValue(
          needsFiltering
            ? await getDepartmentsByParams(
                assetKey,
                isTaskTypeEnabled ? taskTypeId : undefined,
                isPlanDatesEnabled ? plannedStartDate : undefined,
                isPlanDatesEnabled ? plannedEndDate : undefined
              )
            : fullDepartmentsList
        );
      } catch (error) {
        console.error('Error fetching departments:', error);
        setDepartmentPickerError(null);
        setDepartmentsDropDownValue([]);
      }
    },
    [
      showAllDepartments,
      fullDepartmentsList,
      asset,
      taskType,
      plannedStartDate,
      plannedEndDate,
      globalSettings
    ]
  );

  useNewApiCall(
    () => getDepartmentsApi(),
    [showAllDepartments, asset, taskType]
  );

  const getAssetsApi = useCallback(async () => {
    const assets = await AssetsService.getAssetsForSelection();
    if (assets?.data) {
      setAssetOptions(assets.data.map((x) => ({ label: x.label, key: x.key })));
    }
  }, []);

  const getTypesApi = useCallback(async () => {
    const types = await TaskService.getTaskTypes();
    if (types?.data) {
      setTaskTypes(types.data);
    }
  }, []);

  const updateRecommendedEmployeesCount = useCallback((value: unknown) => {
    const normalized = Number(value);
    if (!Number.isNaN(normalized) && normalized >= 0) {
      setRecommendedEmployeesCount(normalized);
      return;
    }
    setRecommendedEmployeesCount(null);
  }, []);

  const loadTaskTypeDetails = useCallback(
    async (taskTypeId: number) => {
      try {
        const { result } = taskTypesService.get(taskTypeId);
        const taskTypeDetails = await result;
        setSelectedTaskTypeDetails(taskTypeDetails ?? null);
        return taskTypeDetails;
      } catch (error) {
        setSelectedTaskTypeDetails(null);
        console.error('Error fetching task type details:', error);
        return undefined;
      }
    },
    [taskTypesService]
  );

  const ensureTaskTypeDetails = useCallback(
    async (taskTypeId: number) => {
      const currentId = Number(selectedTaskTypeDetails?.id);
      if (
        selectedTaskTypeDetails &&
        !Number.isNaN(currentId) &&
        currentId === taskTypeId
      ) {
        return selectedTaskTypeDetails;
      }
      return loadTaskTypeDetails(taskTypeId);
    },
    [loadTaskTypeDetails, selectedTaskTypeDetails]
  );

  const fetchTaskTypeDetails = useCallback(
    async (taskTypeId: number) => {
      try {
        const taskTypeDetails = await loadTaskTypeDetails(taskTypeId);
        if (!taskTypeDetails) return;
        const defaultOperations = taskTypeDetails?.defaultOperations;
        const priorityValue = taskTypeDetails?.priority;
        updateRecommendedEmployeesCount(taskTypeDetails?.requiredEmployees);
        setValue('effort', taskTypeDetails?.effort ?? null, {
          shouldDirty: true,
          shouldValidate: true
        });

        if (priorityValue !== undefined && priorityValue !== null) {
          setValue('priority', priorityValue, {
            shouldDirty: true,
            shouldValidate: true
          });
          const priorityOption = PriorityOptions.find(
            (option) => parseInt(option.key.toString()) === priorityValue
          );
          if (priorityOption) {
            setSelectedPriority(priorityOption);
          }
        }

        setValue(
          'plannedOperations',
          defaultOperations?.map(({ id, label }) => ({
            key: id.toString(),
            label
          })) || [],
          { shouldDirty: true, shouldValidate: true }
        );

        const creationDate = watch('creationDate');
        const baseDate = creationDate ? new Date(creationDate) : new Date();
        const baseTimestamp = baseDate.getTime();

        if (!Number.isNaN(baseTimestamp)) {
          const responseOffset = parseTimeToMilliseconds(
            taskTypeDetails?.responseTime
          );
          const resolutionOffset = parseTimeToMilliseconds(
            taskTypeDetails?.resolutionTime
          );

          if (responseOffset !== null) {
            reset({
              ...formMethods.getValues(),
              plannedStartDate: '',
              plannedEndDate: ''
            });
            setValue(
              'plannedStartDate',
              new Date(baseTimestamp + responseOffset).toISOString(),
              { shouldDirty: true, shouldValidate: true }
            );
          }

          if (resolutionOffset !== null) {
            setValue(
              'plannedEndDate',
              new Date(baseTimestamp + resolutionOffset).toISOString(),
              { shouldDirty: true, shouldValidate: true }
            );
          }
        }
      } catch (error) {
        console.error('Error fetching task type details:', error);
      }
    },
    [
      loadTaskTypeDetails,
      setValue,
      setSelectedPriority,
      updateRecommendedEmployeesCount,
      watch
    ]
  );

  // Use the status transitions service to get the field filling controls
  const statusTransitionsService = useStatusTransitionsService();

  const getFieldFillingControll = useCallback(
    async (transitionId: number) => {
      const fieldFillingControls = await getFieldFillingControlStandalone(
        transitionId,
        statusTransitionsService
      );
      dispatch(updateFieldFillingControll(fieldFillingControls));
    },
    [dispatch, statusTransitionsService]
  );

  const [historyLoaded, setHistoryLoaded] = useState(false);

  // Эффект для загрузки истории при активации вкладки
  useEffect(() => {
    const taskId =
      pageId !== 'newTask' ? pageId : taskProps?.id ?? taskProps.id;
    if (activeIndex === 4 && taskId) {
      setLoading(true); // Используем существующее состояние загрузки
      getTaskHistoryApi(Number(taskId)).finally(() => {
        setLoading(false);
        setHistoryLoaded(true);
      });
    }
  }, [activeIndex, taskProps?.id]);

  const loadHistory = useCallback(() => {
    if (taskProps?.id && !historyLoaded) {
      setHistoryLoaded(false); // Сбрасываем флаг для принудительной перезагрузки
    }
  }, [taskProps?.id, historyLoaded]);

  // Fetch validation rules based on the lastStatusTransitionId
  const getTaskHistoryApi = useCallback(
    async (id: number | undefined) => {
      const taskId =
        pageId !== 'newTask' ? pageId : taskProps?.id ? taskProps.id : id;
      const history = await TaskService.getTaskHistory(taskId as number);
      if (history.data.isSuccess && history.data.value) {
        setTaskHistory(history.data.value);
      }
    },
    [pageId, taskProps]
  );

  const getTaskApi = useCallback(
    async (id: number | undefined, onlyStatus: boolean = false) => {
      const taskId =
        pageId !== 'newTask'
          ? taskProps?.id
            ? taskProps.id
            : Number(pageId)
          : id;
      if (taskId) {
        const task = await TaskService.readTaskById(taskId as number);
        if (task?.data) {
          const taskData = task.data;
          if (onlyStatus) {
            setValue('taskStatus', taskData?.taskStatus ?? null);
            setValue(
              'possibleStatusTransitions',
              taskData?.possibleStatusTransitions ?? []
            );
            setValue('hasAttachments', taskData?.hasAttachments ?? false);
          } else {
            setSelectedPriority({
              key: task.data.priority,
              label:
                PriorityOptions.find(
                  (x) => taskData && x.key === taskData.priority
                )?.label ?? ''
            });
            reset(taskData);
            // TODO фикс несрабатывающей валидации при отсутсвии ключей дат с бэка
            setValue('actualEndDate', taskData.actualEndDate ?? '');
            setValue('plannedEndDate', taskData.plannedEndDate ?? '');
            setValue('actualStartDate', taskData.actualStartDate ?? '');
            setValue('plannedStartDate', taskData.plannedStartDate ?? '');

            setValue('interval', taskData.interval ?? undefined);
            setValue('actualOperations', taskData?.actualOperations || []);
            const taskTypeId = Number(taskData?.taskType?.key);
            if (!Number.isNaN(taskTypeId) && taskTypeId > 0) {
              try {
                const taskTypeDetails = await loadTaskTypeDetails(taskTypeId);
                updateRecommendedEmployeesCount(
                  taskTypeDetails?.requiredEmployees
                );
                setValue('effort', taskTypeDetails?.effort ?? null);
              } catch (error) {
                setRecommendedEmployeesCount(null);
                setValue('effort', null);
              }
            } else {
              setRecommendedEmployeesCount(null);
              setValue('effort', null);
            }
          }

          // Now, after updating the form values, call getFieldFillingControll
          const lastTransitionId = taskData?.lastStatusTransitionId;

          if (lastTransitionId && getFieldFillingControll) {
            await getFieldFillingControll(Number(lastTransitionId));
          }
        }
      }
    },
    [
      pageId,
      taskProps,
      reset,
      setValue,
      getFieldFillingControll,
      loadTaskTypeDetails,
      updateRecommendedEmployeesCount
    ]
  );

  const extractAttainmentIds = (
    operationsAttainments: Array<{
      attainments?: Array<{ id?: number | string }>;
      id?: number;
      operationType?: { id: number };
    }>
  ): number[] => {
    if (!operationsAttainments?.length) {
      return [];
    }
    const ids = new Set<number>();

    for (const item of operationsAttainments) {
      if (item.attainments?.length) {
        for (const attainment of item.attainments) {
          const id =
            typeof attainment.id === 'number'
              ? attainment.id
              : typeof attainment.id === 'string'
              ? parseInt(attainment.id, 10)
              : null;
          if (id && !isNaN(id)) {
            ids.add(id);
          }
        }
      }
    }

    return Array.from(ids);
  };

  const getExecutorsTableApi = useCallback(async () => {
    if (!department?.key) {
      setValue('employees', []);
      setExecutorsTable([]);
      setRawExecutorsData([]);
      setFullExecutorsRaw([]);
      setEmployeePickerError(null);
      fullExecutorsByDepartmentCacheRef.current = null;
      previousDistrictRef.current = '';
      return;
    }

    if (!departmentsDropDownValue?.length) {
      return;
    }

    if (department.key !== previousDistrictRef.current) {
      if (previousDistrictRef.current !== '') {
        setValue('employees', []);
      }
      previousDistrictRef.current = department.key;
    }

    const deptId = parseInt(department.key, 10);
    const deptKey = department.key;
    const taskTypeId = taskType?.key ? Number(taskType.key) : undefined;

    try {
      setEmployeePickerError(null);
      const cache = fullExecutorsByDepartmentCacheRef.current;
      let mappedAll: Employee[] =
        cache?.departmentKey === deptKey ? cache.employees : [];

      if (cache?.departmentKey !== deptKey) {
        const intervalStart = interval?.start;
        const intervalEnd = interval?.end;

        const allEmployeesResponse = await EmployeesService.getByDepartments(
          deptId,
          undefined,
          false,
          undefined,
          plannedStartDate ? plannedStartDate : undefined,
          plannedEndDate ? plannedEndDate : undefined,
          intervalStart ? intervalStart : undefined,
          intervalEnd ? intervalEnd : undefined
        );
        mappedAll = mapEmployeesFromApiResponse(
          allEmployeesResponse?.data?.value
        );
        fullExecutorsByDepartmentCacheRef.current = {
          departmentKey: deptKey,
          employees: mappedAll
        };
        setFullExecutorsRaw(mappedAll);
      }

      if (showAllEmployees) {
        setRawExecutorsData(mappedAll);
      } else {
        const assignmentRulesParams = buildEmployeeAssignmentRulesParams({
          departmentId: deptId,
          plannedStartDate,
          plannedEndDate,
          effort: taskType.effort,
          taskTypeId: taskTypeId,
          attainments: extractAttainmentIds(plannedOperationsAttainments)
        });
        const filteredResponse =
          await EmployeesService.getByDepartmentAssignmentRules(
            assignmentRulesParams
          );
        if (
          filteredResponse.status === 400 &&
          isPlanningAlgorithmError(filteredResponse.data)
        ) {
          setEmployeePickerError(PLANNING_ALGORITHM_ERROR_MESSAGE);
          setRawExecutorsData([]);
          return;
        }
        setRawExecutorsData(
          mapEmployeesFromApiResponse(filteredResponse?.data?.value)
        );
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployeePickerError(null);
      setRawExecutorsData([]);
      setExecutorsTable([]);
      setFullExecutorsRaw([]);
      fullExecutorsByDepartmentCacheRef.current = null;
    }
  }, [
    departmentsDropDownValue,
    department,
    plannedEndDate,
    plannedStartDate,
    setValue,
    showAllEmployees,
    taskType?.key,
    taskType.effort
  ]);

  useNewApiCall(getExecutorsTableApi, [
    departmentsDropDownValue,
    department,
    plannedEndDate,
    plannedStartDate,
    setValue,
    showAllEmployees,
    taskType?.key,
    taskType.effort
  ]);

  useEffect(() => {
    if (rawExecutorsData.length > 0) {
      const employeesWithMappedAttainments = mapEmployeeAttainments(
        rawExecutorsData,
        plannedOperationsAttainments,
        { start: plannedStartDate, end: plannedEndDate }
      );
      setExecutorsTable(employeesWithMappedAttainments);
    } else {
      setExecutorsTable([]);
    }
  }, [
    plannedOperationsAttainments,
    rawExecutorsData,
    plannedStartDate,
    plannedEndDate
  ]);

  const fullExecutorsCatalog = useMemo(
    () =>
      fullExecutorsRaw.length > 0
        ? mapEmployeeAttainments(
            fullExecutorsRaw,
            plannedOperationsAttainments,
            { start: plannedStartDate, end: plannedEndDate }
          )
        : [],
    [
      fullExecutorsRaw,
      plannedOperationsAttainments,
      plannedStartDate,
      plannedEndDate,
      mapEmployeeAttainments
    ]
  );

  // Эффект для фильтрации сотрудников для actualOperations - только выбранные в mainTab
  useEffect(() => {
    if (!employees?.length || !rawExecutorsData.length) {
      setActualOperationsEmployeeOptions([]);
      return;
    }

    const selectedEmployeeIds = new Set(
      employees
        .map((emp) => {
          if (typeof emp === 'object' && emp !== null) {
            const id = (emp as Employee).id || (emp as any).key;
            return id ? id.toString() : null;
          }
          return emp ? String(emp) : null;
        })
        .filter((id): id is string => id !== null && id !== undefined)
    );

    const filtered = rawExecutorsData.filter((emp) => {
      const empId = emp.id?.toString();
      return empId && selectedEmployeeIds.has(empId);
    });

    setActualOperationsEmployeeOptions(filtered);
  }, [employees, rawExecutorsData]);

  const getPlannedOperationsOptions = useCallback(async () => {
    const result = await TaskService.getOperationListForSelection(true);
    if (result?.data) {
      const operationsWithId = result.data.map(
        (x) => ({ ...x, id: parseInt(x.key) }) as any
      );
      setPlannedOperationsOptions(operationsWithId);

      const allAttainments =
        convertOperationsOptionsToAttainments(operationsWithId);
      setAllOperationsAttainments(allAttainments);
    }
  }, []);

  const callApi = useCallback(
    async (id: number | undefined) => {
      setLoading(true);
      await Promise.all([
        getAssetsApi(),
        getTypesApi(),
        getTaskApi(id),
        getPlannedOperationsOptions()
      ]);

      setLoading(false);
    },
    [getAssetsApi, getTypesApi, getTaskApi, getPlannedOperationsOptions]
  );

  const toggleShowAllDepartments = useCallback(() => {
    setShowAllDepartments((prev) => !prev);
  }, []);

  const toggleShowAllEmployees = useCallback(() => {
    setShowAllEmployees((prev) => !prev);
  }, []);

  return {
    showAllDepartments,
    toggleShowAllDepartments,
    showAllEmployees,
    toggleShowAllEmployees,
    fullDepartmentsTree: fullDepartmentsList,
    fullExecutorsCatalog,
    departmentPickerError,
    employeePickerError,
    departmentsDropDownValue,
    loading,
    assetOptions,
    taskTypes,
    fieldFillingControll,
    taskHistory,
    recommendedEmployeesCount,
    selectedPriority,
    setSelectedPriority,
    getAssetsApi,
    getTaskApi,
    fetchTaskTypeDetails,
    ensureTaskTypeDetails,
    selectedTaskTypeDetails,
    loadHistory,
    getFieldFillingControll,
    executorsTable,
    rawExecutorsData,
    plannedOperationsOptions,
    plannedOperationsAttainments,
    allOperationsAttainments,
    actualOperationsEmployeeOptions,
    callApi,
    form: { reset, setValue, watch, ...formMethods }
  };
}
